import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Menú «⋯» de acciones de una fila de Trabajos.
 *
 * Nació porque cada fila terminaba en ocho botones de solo ícono, en fila, sin
 * texto. Nadie recuerda qué hace el frasco ni el clip: para usarlos había que
 * apoyar el mouse encima de cada uno y esperar el tooltip, de a uno. Y esos
 * ocho íconos se comían el ancho que necesitaban el cliente y la dirección.
 *
 * Dentro del menú cada acción tiene su NOMBRE escrito, que es lo que la vuelve
 * usable. Afuera de la fila quedan solo las acciones principales de cada estado
 * («Cerrar trabajo», «Cerrar caso», «Mandar técnico»), que sí llevan texto.
 *
 * Vive en components/jobs/ y no en components/ui/ a propósito: es un menú
 * chiquito hecho para esta fila, no un componente de sistema. Promoverlo a
 * ui/ recién tendría sentido cuando una segunda pantalla lo pida — y ahí se
 * discute accesibilidad completa, submenús y navegación por teclado.
 *
 * Acá adentro NO van acciones que la guía en vivo ilumine: no se puede
 * iluminar algo que está dentro de un menú cerrado, el usuario vería el foco
 * puesto en la nada. Esas se quedan como íconos en la fila.
 */

export interface AccionDeMenu {
  /** Texto de la acción, escrito completo. */
  label: string;
  /** Ícono de lucide-react ya renderizado. */
  icono: ReactNode;
  onClick: () => void;
  /** Rojo: acciones que destruyen algo. */
  peligrosa?: boolean;
  /** Texto de ayuda extra (aparece debajo, en chico). */
  ayuda?: string;
}

export function MenuAcciones({
  acciones,
  etiqueta = 'Más acciones',
  className,
}: {
  acciones: AccionDeMenu[];
  etiqueta?: string;
  className?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Cerrar al tocar afuera o con Escape. Sin esto, el menú de una fila se queda
  // abierto mientras se usa otra y tapa la lista.
  useEffect(() => {
    if (!abierto) return;
    const alTocarAfuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', alTocarAfuera);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alTocarAfuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  if (acciones.length === 0) return null;

  return (
    <div ref={contenedor} className={cn('relative', className)}>
      <button
        type="button"
        aria-label={etiqueta}
        title={etiqueta}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={abierto ? menuId : undefined}
        data-guia="jobs.fila.acciones"
        onClick={() => setAbierto((v) => !v)}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition-colors',
          'hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200',
          abierto && 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
        )}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {abierto && (
        <div
          id={menuId}
          role="menu"
          // A la izquierda y hacia abajo: el menú está al final de la fila, y
          // abriéndolo hacia la derecha se saldría de la pantalla.
          className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-lg border border-surface-border bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {acciones.map((accion) => (
            <button
              key={accion.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setAbierto(false);
                accion.onClick();
              }}
              className={cn(
                'flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors',
                accion.peligrosa
                  ? 'text-danger hover:bg-red-50 dark:hover:bg-red-900/20'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800',
              )}
            >
              <span className="mt-0.5 shrink-0">{accion.icono}</span>
              <span className="min-w-0">
                {accion.label}
                {accion.ayuda && (
                  <span className="block text-xs text-slate-400">{accion.ayuda}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
